<template>
  <div v-if="hasDownloads" class="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border w-96 z-50">
    <div class="p-4">
      <!-- Header -->
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold text-gray-800">
          Downloads
          <span class="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
            {{ totalDownloads }}
          </span>
        </h3>
        <button 
          @click="isMinimized = !isMinimized"
          class="text-gray-500 hover:text-gray-700"
        >
          <i :class="isMinimized ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </button>
      </div>
      
      <!-- Downloads List -->
      <div v-if="!isMinimized" class="space-y-3 max-h-80 overflow-y-auto">
        <div 
          v-for="download in allDownloads" 
          :key="download.id"
          class="bg-gray-50 rounded-lg p-3"
        >
          <!-- File Info -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate" :title="download.fileName">
                {{ download.fileName }}
              </p>
              <div class="flex items-center gap-2 text-xs text-gray-500">
                <span>{{ fileStore.formatFileSize(download.fileSize) }}</span>
                <span class="px-1.5 py-0.5 rounded text-xs" :class="getTypeClass(download.type)">
                  <i :class="getTypeIcon(download.type)" class="mr-1"></i>
                  {{ getTypeLabel(download.type) }}
                </span>
                <span v-if="download.isOffline" class="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                  <i class="fas fa-check-circle mr-1"></i>
                  Available Offline
                </span>
              </div>
            </div>
            
            <!-- Actions -->
            <div class="flex items-center gap-1 ml-2">
              <button 
                v-if="canResume(download)"
                @click="resumeDownload(download)"
                class="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                title="Resume download"
              >
                <i class="fas fa-play text-xs"></i>
              </button>
              <button 
                v-if="canPause(download)"
                @click="pauseDownload(download)"
                class="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded"
                title="Pause download"
              >
                <i class="fas fa-pause text-xs"></i>
              </button>
              <button 
                @click="cancelDownload(download)"
                class="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                title="Cancel download"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
          
          <!-- Progress Bar -->
          <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div 
              class="h-2 rounded-full transition-all duration-300"
              :class="getProgressBarClass(download.status)"
              :style="{ width: `${download.progress}%` }"
            ></div>
          </div>
          
          <!-- Progress Info -->
          <div class="flex justify-between items-center text-xs text-gray-500">
            <span>{{ download.progress }}%</span>
            <span class="flex items-center gap-2">
              <span v-if="download.chunks">
                {{ download.chunks.downloaded }}/{{ download.chunks.total }} chunks
              </span>
              <span :class="getStatusClass(download.status)">
                {{ getStatusLabel(download.status) }}
              </span>
            </span>
          </div>
        </div>
        
        <!-- Empty State -->
        <div v-if="allDownloads.length === 0" class="text-center py-4 text-gray-500">
          <i class="fas fa-download text-2xl mb-2"></i>
          <p class="text-sm">No active downloads</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useFileStore } from '../stores/fileStore'
import type { DownloadItem, DownloadStatus, DownloadType } from '../types/download'

const isMinimized = ref<boolean>(false)
const fileStore = useFileStore()

// Get all data from the unified store - currently returns empty array
const allDownloads = computed((): DownloadItem[] => fileStore.allDownloads || [])
const totalDownloads = computed((): number => allDownloads.value.length)
const hasDownloads = computed((): boolean => totalDownloads.value > 0)

// Download actions
async function resumeDownload(download: DownloadItem): Promise<void> {
  try {
    if (fileStore.resumeDownload) {
      await fileStore.resumeDownload(download.id)
    } else {
      console.warn('resumeDownload method not implemented in fileStore')
    }
  } catch (error) {
    console.error('Failed to resume download:', error)
  }
}

async function pauseDownload(download: DownloadItem): Promise<void> {
  try {
    if (fileStore.pauseDownload) {
      await fileStore.pauseDownload(download.id)
    } else {
      console.warn('pauseDownload method not implemented in fileStore')
    }
  } catch (error) {
    console.error('Failed to pause download:', error)
  }
}

async function cancelDownload(download: DownloadItem): Promise<void> {
  try {
    if (fileStore.cancelDownload) {
      await fileStore.cancelDownload(download.id)
    } else {
      console.warn('cancelDownload method not implemented in fileStore')
    }
  } catch (error) {
    console.error('Failed to cancel download:', error)
  }
}

// Helper functions
function canResume(download: DownloadItem): boolean {
  return download.status !== 'completed' && download.status !== 'downloading' && download.status !== 'starting'
}

function canPause(download: DownloadItem): boolean {
  return download.status === 'downloading' || download.status === 'starting'
}

function getTypeClass(type: DownloadType): string {
  const classes: Record<DownloadType, string> = {
    'private': 'bg-blue-100 text-blue-700',
    'public': 'bg-green-100 text-green-700',
    'offline': 'bg-purple-100 text-purple-700',
    'public-offline': 'bg-indigo-100 text-indigo-700',
    'google-drive': 'bg-yellow-100 text-yellow-700'
  }
  return classes[type] || 'bg-gray-100 text-gray-700'
}

function getTypeIcon(type: DownloadType): string {
  const icons: Record<DownloadType, string> = {
    'private': 'fas fa-lock',
    'public': 'fas fa-globe',
    'offline': 'fas fa-cloud-download-alt',
    'public-offline': 'fas fa-cloud-download-alt',
    'google-drive': 'fab fa-google-drive'
  }
  return icons[type] || 'fas fa-download'
}

function getTypeLabel(type: DownloadType): string {
  const labels: Record<DownloadType, string> = {
    'private': 'Private',
    'public': 'Public',
    'offline': 'Make Offline',
    'public-offline': 'Make Offline',
    'google-drive': 'Google Drive'
  }
  return labels[type] || 'Download'
}

function getProgressBarClass(status: DownloadStatus): string {
  const classes: Record<DownloadStatus, string> = {
    'starting': 'bg-blue-400',
    'downloading': 'bg-blue-600',
    'paused': 'bg-yellow-500',
    'completed': 'bg-green-600',
    'error': 'bg-red-600',
    'initiated': 'bg-orange-500',
    'stalled': 'bg-red-400'
  }
  return classes[status] || 'bg-blue-600'
}

function getStatusClass(status: DownloadStatus): string {
  const classes: Record<DownloadStatus, string> = {
    'starting': 'text-blue-600',
    'downloading': 'text-blue-600',
    'paused': 'text-yellow-600',
    'completed': 'text-green-600',
    'error': 'text-red-600',
    'initiated': 'text-orange-600',
    'stalled': 'text-red-500'
  }
  return classes[status] || 'text-gray-600'
}

function getStatusLabel(status: DownloadStatus): string {
  const labels: Record<DownloadStatus, string> = {
    'starting': 'Starting...',
    'downloading': 'Downloading...',
    'paused': 'Paused',
    'completed': 'Completed',
    'error': 'Error',
    'initiated': 'Ready to Resume',
    'stalled': 'Stalled'
  }
  return labels[status] || status || 'Unknown'
}
</script> 